/**
 * Рисует картинку расклада для «поделиться».
 *
 * Рендерим на клиенте, а не на сервере: карточка повторяет то, что человек
 * видит на экране, и для этого не нужен ни headless-браузер, ни нативный
 * canvas на бэкенде.
 *
 * Квадрат, а не формат историй.
 *
 * Telegram показывает предпросмотр сообщения на всю ширину пузыря, а высоту
 * берёт из пропорций картинки. При 9:16 предпросмотр занимал почти весь
 * экран и список чатов уезжал за нижний край — выбрать получателя было
 * нельзя. При 3:4 всё ещё оставался высоким. Квадрат делает предпросмотр
 * ровно в ширину пузыря. Основной способ поделиться у нас чат, история —
 * запасной, и там квадрат тоже смотрится нормально.
 */

import { artUrl, hasArt } from './cardArt'

const W = 1080
const H = 1080

export interface ShareCardCard {
  id: number
  sym: string
  rank: string
  name: string
  label: string
  reversed: boolean
}

export interface ShareCardData {
  title: string
  cards: ShareCardCard[]
  text: string
  tagline: string
  date: string
}

const GOLD = '#c8a24e'
const GOLD_BRIGHT = '#e6c874'
const INK = '#efe3c6'
const INK_SOFT = '#cdbfe0'

/** Шрифты подключены через <link>; до отрисовки их надо дождаться. */
async function ensureFonts(): Promise<void> {
  const needed = [
    '700 italic 84px "Playfair Display"',
    '500 italic 56px "Playfair Display"',
    '600 40px "Cormorant Garamond"',
    '600 26px Mulish',
  ]
  try {
    await Promise.all(needed.map((f) => document.fonts.load(f, 'SIRR Таро')))
    await document.fonts.ready
  } catch {
    // Шрифты не загрузились — рисуем системными, картинка всё равно выйдет.
  }
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}

/** Переносит текст по словам и возвращает строки, обрезая по максимуму. */
function wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, maxLines: number): string[] {
  const words = text.split(/\s+/)
  const lines: string[] = []
  let line = ''

  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word
    if (ctx.measureText(candidate).width <= maxWidth) {
      line = candidate
      continue
    }
    if (line) lines.push(line)
    line = word
    if (lines.length === maxLines) break
  }
  if (lines.length < maxLines && line) lines.push(line)

  if (lines.length === maxLines) {
    // Последняя строка могла оборваться на полуслове — ставим многоточие.
    let last = lines[maxLines - 1]
    const consumed = lines.join(' ').split(/\s+/).length
    if (consumed < words.length) {
      while (last && ctx.measureText(`${last}…`).width > maxWidth) {
        last = last.slice(0, -1).trimEnd()
      }
      lines[maxLines - 1] = `${last}…`
    }
  }
  return lines
}

function drawBackground(ctx: CanvasRenderingContext2D) {
  const bg = ctx.createRadialGradient(W / 2, H * 0.22, 60, W / 2, H * 0.22, H * 0.85)
  bg.addColorStop(0, '#2a1550')
  bg.addColorStop(0.55, '#170c2b')
  bg.addColorStop(1, '#0b0714')
  ctx.fillStyle = bg
  ctx.fillRect(0, 0, W, H)

  // Звёзды — детерминированные, чтобы одинаковый расклад давал ту же картинку.
  let seed = 20260719
  const rand = () => {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff
    return seed / 0x7fffffff
  }
  for (let i = 0; i < 140; i++) {
    const x = rand() * W
    const y = rand() * H
    const r = rand() * 2.4 + 0.6
    ctx.globalAlpha = 0.15 + rand() * 0.55
    ctx.fillStyle = '#e9d9a8'
    ctx.beginPath()
    ctx.arc(x, y, r, 0, Math.PI * 2)
    ctx.fill()
  }
  ctx.globalAlpha = 1

  ctx.strokeStyle = 'rgba(200,162,78,.45)'
  ctx.lineWidth = 2
  roundRect(ctx, 44, 44, W - 88, H - 88, 40)
  ctx.stroke()
}

/**
 * Загружает иллюстрации заранее: canvas рисует только то, что уже в памяти.
 * Карта без картинки или с неудавшейся загрузкой просто выйдет с символом —
 * ради «поделиться» ронять всю отрисовку незачем.
 */
async function loadArt(cards: ShareCardCard[]): Promise<Map<number, HTMLImageElement>> {
  const loaded = new Map<number, HTMLImageElement>()
  await Promise.all(
    [...new Set(cards.map((c) => c.id))]
      .filter(hasArt)
      .map(
        (id) =>
          new Promise<void>((resolve) => {
            const img = new Image()
            img.onload = () => {
              loaded.set(id, img)
              resolve()
            }
            img.onerror = () => resolve()
            img.src = artUrl(id, 'lg')
          }),
      ),
  )
  return loaded
}

/**
 * Вписывает иллюстрацию в рамку с обрезкой по краям: пропорции рисунка и
 * рамки совпадают неточно, а поля вокруг картинки смотрелись бы неряшливо.
 */
function drawArt(ctx: CanvasRenderingContext2D, img: HTMLImageElement, c: ShareCardCard,
                 x: number, y: number, w: number, h: number, r: number) {
  const scale = Math.max(w / img.width, h / img.height)
  const dw = img.width * scale
  const dh = img.height * scale

  ctx.save()
  roundRect(ctx, x, y, w, h, r)
  ctx.clip()
  ctx.translate(x + w / 2, y + h / 2)
  if (c.reversed) ctx.rotate(Math.PI)
  ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh)
  ctx.restore()

  // Та же обработка, что и на экране: приглушаем светлую бумагу и сажаем её
  // на тёмный фон виньеткой, иначе карточка выглядит вырезкой из другого макета.
  ctx.save()
  roundRect(ctx, x, y, w, h, r)
  ctx.clip()
  ctx.fillStyle = 'rgba(38,20,58,.22)'
  ctx.fillRect(x, y, w, h)
  const vignette = ctx.createRadialGradient(x + w / 2, y + h * 0.45, w * 0.2, x + w / 2, y + h * 0.45, w * 0.78)
  vignette.addColorStop(0, 'rgba(20,10,31,0)')
  vignette.addColorStop(1, 'rgba(20,10,31,.62)')
  ctx.fillStyle = vignette
  ctx.fillRect(x, y, w, h)
  ctx.restore()
}

function drawCardFace(ctx: CanvasRenderingContext2D, c: ShareCardCard, x: number, y: number, w: number, h: number,
                      art?: HTMLImageElement) {
  const grad = ctx.createLinearGradient(x, y, x + w, y + h)
  grad.addColorStop(0, '#251639')
  grad.addColorStop(1, '#140a25')
  ctx.fillStyle = grad
  roundRect(ctx, x, y, w, h, w * 0.09)
  ctx.fill()
  ctx.strokeStyle = 'rgba(200,162,78,.55)'
  ctx.lineWidth = 2
  ctx.stroke()

  const pad = w * 0.07
  const artX = x + pad
  const artY = y + pad
  const artW = w - pad * 2
  const artH = h - pad * 2 - h * 0.16
  const artR = w * 0.06

  if (art) drawArt(ctx, art, c, artX, artY, artW, artH, artR)

  ctx.strokeStyle = 'rgba(200,162,78,.35)'
  ctx.lineWidth = 1.5
  roundRect(ctx, artX, artY, artW, artH, artR)
  ctx.stroke()

  // Ранг в углу.
  ctx.fillStyle = 'rgba(214,178,94,.75)'
  ctx.font = `600 ${Math.round(w * 0.1)}px Mulish, sans-serif`
  ctx.textAlign = 'right'
  ctx.fillText(c.rank, x + w - pad * 1.6, y + pad * 2.4)

  // Без иллюстрации карта показывает символ масти; перевёрнутая — вверх
  // ногами, как на экране.
  const cx = x + w / 2
  if (!art) {
    const cy = y + (h - h * 0.16) / 2 + pad * 0.4
    ctx.save()
    ctx.translate(cx, cy)
    if (c.reversed) ctx.rotate(Math.PI)
    ctx.fillStyle = GOLD_BRIGHT
    ctx.font = `${Math.round(w * 0.44)}px serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(c.sym, 0, 0)
    ctx.restore()
  }

  ctx.textBaseline = 'alphabetic'
  ctx.textAlign = 'center'
  ctx.fillStyle = INK
  ctx.font = `500 italic ${Math.round(w * 0.115)}px "Playfair Display", Georgia, serif`
  const name = wrap(ctx, c.name, w - pad, 2)
  name.forEach((line, i) => {
    ctx.fillText(line, cx, y + h - pad * 0.9 - (name.length - 1 - i) * w * 0.13)
  })
}

/** Рисует карточку расклада и отдаёт PNG. */
export async function renderShareCard(data: ShareCardData): Promise<Blob> {
  const [, art] = await Promise.all([ensureFonts(), loadArt(data.cards)])

  const canvas = document.createElement('canvas')
  canvas.width = W
  canvas.height = H
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas недоступен')

  drawBackground(ctx)

  // ── Шапка ──────────────────────────────────────────────────────────
  ctx.textAlign = 'center'
  ctx.fillStyle = INK
  ctx.font = '700 italic 96px "Playfair Display", Georgia, serif'
  ctx.fillText('SIRR', W / 2, 170)

  ctx.fillStyle = GOLD
  ctx.font = '600 26px Mulish, sans-serif'
  ctx.letterSpacing = '10px'
  ctx.fillText(data.tagline.toUpperCase(), W / 2, 214)
  ctx.letterSpacing = '0px'

  // ── Раскладка ──────────────────────────────────────────────────────
  // Считаем высоту всего блока заранее и центрируем его между шапкой и
  // подвалом: иначе при трёх картах низ карточки пустует на треть высоты,
  // а при двенадцати — наоборот, впритык.
  const n = data.cards.length
  // В квадрат три ряда карт не помещаются: раскладываем максимум в два,
  // отсюда и число карт в ряду.
  const gap = 28
  const labelH = 44
  const titleH = 80
  const textGap = 56
  const lineH = 60

  ctx.font = '500 44px "Cormorant Garamond", Georgia, serif'
  const headerBottom = 210
  const footerTop = H - 130

  /**
   * **Размер карты считается ПЕРВЫМ, а не из остатка.**
   *
   * Раньше высоту сперва резали под заголовок и три строки толкования, а
   * картам доставалось уцелевшее: три карты выходили по 183 пикселя на картинке
   * в 1080, двенадцать — по 90. То есть карты, ради которых человек и делится
   * раскладом, занимали от восьмой до шестой части ширины и не читались вовсе.
   *
   * Теперь наоборот: картам отдаётся всё, кроме заголовка, подвала и двух строк
   * толкования, — а сколько строк текста поместится сверх этого, решает
   * остаток. Толкование на карточке всё равно приманка, а не чтение: целиком
   * его читают в приложении.
   */
  const roomTotal = footerTop - headerBottom - titleH - textGap
  const roomForCards = roomTotal - lineH * 2

  /**
   * Раскладку выбираем перебором, а не формулой.
   *
   * Прежнее правило «до трёх — в ряд, дальше пополам» давало на четырёх картах
   * два ряда по две, и карта выходила 118 пикселей — хотя в один ряд те же
   * четыре влезают по 219. На квадрате упирается обычно высота, поэтому лишний
   * ряд почти всегда хуже длинного. Считаем все варианты и берём тот, где карта
   * крупнее; при равенстве — с меньшим числом рядов.
   */
  let perRow = n
  let rows = 1
  let cardW = 0
  for (let candidate = 1; candidate <= n; candidate++) {
    const r = Math.ceil(n / candidate)
    const byHeight = (roomForCards - (r - 1) * (gap + labelH) - labelH) / r
    // Поля по бокам — 120: прежние 160 съедали ширину, которой как раз
    // не хватало картам.
    const byWidth = Math.floor((W - 120 - gap * (candidate - 1)) / candidate)
    const w = Math.max(90, Math.min(300, byWidth, Math.floor(byHeight / 1.55)))
    if (w > cardW) {
      cardW = w
      perRow = candidate
      rows = r
    }
  }
  const cardH = Math.round(cardW * 1.55)

  const cardsH = rows * cardH + (rows - 1) * (gap + labelH) + labelH
  const roomForText = footerTop - headerBottom - titleH - cardsH - textGap
  const lines = wrap(ctx, data.text, W - 200, Math.max(2, Math.min(9, Math.floor(roomForText / lineH))))
  const textH = lines.length * lineH

  const contentH = titleH + cardsH + textGap + textH
  const startY = headerBottom + Math.max(0, (footerTop - headerBottom - contentH) / 2)

  ctx.fillStyle = INK
  ctx.font = '500 italic 62px "Playfair Display", Georgia, serif'
  ctx.textAlign = 'center'
  ctx.fillText(data.title, W / 2, startY + 62)

  const blockTop = startY + titleH

  for (let i = 0; i < n; i++) {
    const row = Math.floor(i / perRow)
    const inRow = Math.min(perRow, n - row * perRow)
    const rowW = inRow * cardW + (inRow - 1) * gap
    const startX = (W - rowW) / 2
    const col = i % perRow
    const x = startX + col * (cardW + gap)
    const y = blockTop + row * (cardH + gap + labelH)

    drawCardFace(ctx, data.cards[i], x, y, cardW, cardH, art.get(data.cards[i].id))

    if (data.cards[i].label) {
      ctx.fillStyle = 'rgba(214,178,94,.85)'
      ctx.font = `600 ${Math.min(24, Math.round(cardW * 0.11))}px Mulish, sans-serif`
      ctx.textAlign = 'center'
      ctx.letterSpacing = '2px'
      ctx.fillText(data.cards[i].label.toUpperCase(), x + cardW / 2, y + cardH + 32)
      ctx.letterSpacing = '0px'
    }
  }

  // ── Толкование ─────────────────────────────────────────────────────
  const textTop = blockTop + cardsH + textGap
  ctx.fillStyle = GOLD
  ctx.font = '600 32px Mulish, sans-serif'
  ctx.textAlign = 'center'
  ctx.fillText('✦', W / 2, textTop - 46)

  ctx.fillStyle = INK_SOFT
  ctx.font = '500 44px "Cormorant Garamond", Georgia, serif'
  lines.forEach((line, i) => ctx.fillText(line, W / 2, textTop + i * lineH))

  // ── Подвал ─────────────────────────────────────────────────────────
  const footerY = H - 130
  ctx.strokeStyle = 'rgba(200,162,78,.28)'
  ctx.lineWidth = 1.5
  ctx.beginPath()
  ctx.moveTo(W / 2 - 150, footerY - 46)
  ctx.lineTo(W / 2 + 150, footerY - 46)
  ctx.stroke()

  ctx.fillStyle = 'rgba(214,178,94,.8)'
  ctx.font = '600 26px Mulish, sans-serif'
  ctx.letterSpacing = '6px'
  ctx.textAlign = 'center'
  ctx.fillText(data.date.toUpperCase(), W / 2, footerY)
  ctx.letterSpacing = '0px'

  // JPEG, а не PNG: фон — градиент с «шумом» звёзд, PNG на нём разрастается
  // до полутора мегабайт, тогда как JPEG укладывается в сотни килобайт при
  // неотличимом качестве. Прозрачность карточке не нужна.
  //
  // Качество 0.8, а не 0.92: с иллюстрациями карт Колесо года весило под
  // 560 КБ, и Telegram, скачивая картинку с нашего сервера, успевал получить
  // только часть — в чате оставалась половина изображения и серый блок.
  // На глаз разница незаметна, вес падает вдвое.
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Не удалось создать изображение'))),
      'image/jpeg',
      0.8,
    )
  })
}
